import {
  Agenda,
  Avatar,
  Celebration,
  DayHistory,
  Heading,
  Icon,
  PointsPill,
  ProgressRing,
  StarJar,
  Surface,
  Text,
  WeekList,
  buttonStyles,
  cn,
} from '@shh/ui';
import { Fire, Gift } from '@phosphor-icons/react';
import { STREAK_WINDOW } from '@shh/shared';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { EventSheet } from '../components/EventSheet';
import { RewardShop } from '../components/Rewards';
import { PersonTasks, useTick } from '../components/tasks';
import { TimetableView } from '../components/TimetableView';
import { agendaItems, weekDays } from '../data/agenda';
import { useClock } from '../data/clock';
import {
  currentDaypart,
  goalRewardOf,
  memberById,
  streakOf,
  unitFor,
  type M,
} from '../data/derive';
import { useFamily } from '../data/family';
import { useHistory } from '../data/history';
import { tasksText } from '../i18n/tasks';
import { todayText } from '../i18n/today';
import { calendarText } from '../i18n/calendar';

/** For the celebration: the routine that was just finished ("Morgen" alone reads like tomorrow). */
const LABEL = todayText.routines;
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * One person, one level below the dashboard. Same building blocks as everywhere, only
 * filtered: their day (incl. school), their tasks, timetable, week and rewards.
 */
export function PersonPage() {
  const { id } = useParams();
  const f = useFamily();
  const member = memberById(f, id);
  if (!member) return f.loaded ? <Navigate to="/" replace /> : null;
  return <PersonView member={member} />;
}

function PersonView({ member }: { member: M }) {
  const f = useFamily();
  const now = useClock(f.timeZone);
  const navigate = useNavigate();
  // with the day it was tapped on: series and the week list open events on other days
  const [openEvent, setOpenEvent] = useState<{ id: string; date: string } | null>(null);
  const [celebrate, setCelebrate] = useState<M | null>(null);
  const { tick, together, picker } = useTick(setCelebrate);
  const isParent = member.role === 'parent';
  const unit = unitFor(member);
  const goal = isParent ? undefined : goalRewardOf(f, member);
  const current = currentDaypart(now);
  const today = agendaItems(
    f,
    f.today,
    now,
    member.id,
    (e) => (e.bring?.length ? <span>{calendarText.bring(e.bring.join(', '))}</span> : null),
    {
      onlyLens: true,
    },
  );
  const timetable = f.timetables[member.id];
  const wishes = f.wishes.filter((w) => w.owner === member.id && !w.received).length;

  return (
    <>
      <div className="flex flex-wrap items-center gap-gap-lg">
        <Avatar name={member.name} color={member.color} emoji={member.emoji} size="xl" decorative />
        <div className="flex flex-col gap-gap-sm">
          <Heading level={2} size="xl">
            {member.name}
          </Heading>
          <div className="flex flex-wrap items-center gap-gap-sm">
            {!isParent && <PointsPill points={member.balance} unit={unit} size="lg" />}
            {/* Freizeit (1.10): everybody has a wish list, one tap away */}
            <Link
              to={`/freizeit/wuensche/${member.id}`}
              className={cn(buttonStyles({ variant: 'secondary' }), 'text-sm')}
            >
              <Icon icon={Gift} size="sm" /> {tasksText.wishList}
              {wishes > 0 && <span className="text-text-secondary">({wishes})</span>}
            </Link>
          </div>
        </div>
        {goal &&
          (member.ageProfile === 'picture' ? (
            <Surface className="w-full sm:ml-auto sm:w-auto">
              <StarJar stars={member.balance} goal={goal.cost} goalLabel={goal.title} />
            </Surface>
          ) : (
            <Surface className="flex w-full items-center gap-gap-md sm:ml-auto sm:w-auto">
              <ProgressRing
                value={member.balance}
                max={goal.cost}
                label={tasksText.progressTo(goal.title)}
                valueLabel={tasksText.balanceOf(String(member.balance), String(goal.cost), unit)}
              >
                <span className="text-sm">{member.balance}</span>
              </ProgressRing>
              <div className="flex flex-col">
                <Text size="sm" tone="secondary">
                  {tasksText.nextGoal}
                </Text>
                <span className="font-display text-lg font-black">{goal.title}</span>
                <Text size="sm" tone="secondary">
                  {member.balance >= goal.cost
                    ? tasksText.reached
                    : tasksText.left(String(goal.cost - member.balance))}
                </Text>
              </div>
            </Surface>
          ))}
      </div>

      <div className="grid grid-cols-1 gap-section xl:grid-cols-2">
        <section aria-labelledby="p-day" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="p-day">
            {tasksText.myDay}
          </Heading>
          {today.length ? (
            <Agenda
              items={today}
              nowLabel={now}
              onItemPress={(id) => setOpenEvent({ id, date: f.today })}
            />
          ) : (
            <Surface tone="sunken">
              <Text>{tasksText.noEventsToday}</Text>
            </Surface>
          )}
        </section>

        <section aria-labelledby="p-tasks" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="p-tasks">
            {isParent ? tasksText.myTodos : tasksText.myTasks}
          </Heading>
          {isParent ? (
            <PersonTasks
              member={member}
              daypart="all"
              onTick={tick}
              onTogether={together}
              maxItems={20}
              showHeader={false}
            />
          ) : (
            <PersonTasks
              member={member}
              daypart="all"
              onTick={tick}
              onTogether={together}
              maxItems={30}
              showHeader={false}
            />
          )}
        </section>
      </div>

      {!isParent && <LookingBack member={member} />}

      {timetable && (
        <section aria-labelledby="p-tt" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="p-tt">
            {timetable.label === 'Kita' ? tasksText.kitaTimes : tasksText.timetable}
          </Heading>
          <Surface padding="sm">
            <TimetableView timetable={timetable} today={f.today} />
          </Surface>
        </section>
      )}

      <section aria-labelledby="p-week" className="flex flex-col gap-gap-md">
        <Heading level={2} size="md" id="p-week">
          {tasksText.myWeek}
        </Heading>
        <Surface padding="sm">
          <WeekList
            days={weekDays(f, 1, 6, member.id)}
            onItemPress={(id, date) => setOpenEvent({ id, date })}
          />
        </Surface>
      </section>

      {!isParent && (
        <section aria-labelledby="p-shop" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="p-shop">
            {tasksText.rewards}
          </Heading>
          <RewardShop member={member} unit={unit} onEarnMore={() => navigate('/aufgaben')} />
        </section>
      )}

      <EventSheet
        eventId={openEvent?.id ?? null}
        date={openEvent?.date ?? f.today}
        onClose={() => setOpenEvent(null)}
      />
      {picker}
      <Celebration
        show={!!celebrate}
        title={tasksText.routineDone(capital(LABEL[current]))}
        subtitle={tasksText.wellDone}
        onDone={() => setCelebrate(null)}
      />
    </>
  );
}

/** Kids and teens: the last seven days and the longest streak running right now. */
function LookingBack({ member }: { member: M }) {
  const f = useFamily();
  const days = useHistory(member.id);
  const best = f.tasks
    .map((t) => ({ task: t, count: streakOf(f, member.id, t) ?? 0 }))
    .sort((a, b) => b.count - a.count)[0];
  return (
    <section aria-labelledby="p-back" className="flex flex-col gap-gap-md">
      <Heading level={2} size="md" id="p-back">
        {tasksText.last7}
      </Heading>
      <Surface className="flex flex-col gap-gap-md">
        {best && best.count > 0 && (
          <Text className="flex flex-wrap items-center gap-gap-xs">
            <Icon icon={Fire} size="md" weight="fill" className="text-reward-streak" />
            <strong>
              {tasksText.streak(best.count >= STREAK_WINDOW ? '60+' : String(best.count))}
            </strong>{' '}
            {best.task.title}
          </Text>
        )}
        {days ? (
          <DayHistory days={days} className="max-w-narrow" />
        ) : (
          <Text tone="muted">{tasksText.loading}</Text>
        )}
      </Surface>
    </section>
  );
}
